<template>
  <div class="card" data-testid="partner-logo-form">
    <h2 class="mb-3 text-headline-m">{{ t('PartnerCompany.webLogo') }}</h2>
    <hr class="dark:bg-gray-700 my-4 h-px border-0 bg-surface-elevation-5" />
    <div class="flex h-36 min-w-0 gap-3">
      <img class="h-36 w-36 shrink-0 rounded-sm object-contain" :src="previewWebLogoUrl" data-testid="partner-logo-preview" />
      <div class="flex min-w-0 flex-col justify-center">
        <p class="text-label-m text-subtlest">{{ t('PartnerCompany.webLogoHint') }}</p>
        <label class="mt-2 block max-w-full min-w-0 cursor-pointer" data-testid="partner-logo-upload">
          <span class="btn-outline-neutral-xs max-w-full overflow-hidden">
            <UploadIcon :size="14" />
            <span class="truncate txt-body-s-medium">{{ getWebLogoFilename }}</span>
          </span>
          <input type="file" class="hidden" :accept="webLogoAcceptedFormats" @change="handleWebLogoChange" />
        </label>
        <div v-if="v$.logos.webLogo.$errors.length" class="mt-2 text-label-m text-error">
          <div v-for="error in v$.logos.webLogo.$errors" :key="error.$message">{{ error.$message }}</div>
        </div>
      </div>
    </div>
  </div>

  <div class="mt-4 card" data-testid="partner-alt-logo-form">
    <h2 class="mb-3 text-headline-m">{{ t('PartnerCompany.logo') }}</h2>
    <hr class="dark:bg-gray-700 my-4 h-px border-0 bg-surface-elevation-5" />
    <div class="flex h-36 min-w-0 gap-3">
      <img class="h-36 w-36 shrink-0 rounded-sm object-cover" :src="previewLogoUrl" data-testid="partner-alt-logo-preview" />
      <div class="flex min-w-0 flex-col justify-center">
        <p class="text-label-m text-subtlest">{{ t('PartnerCompany.logoHint') }}</p>
        <label class="mt-2 block max-w-full min-w-0 cursor-pointer" data-testid="partner-alt-logo-upload">
          <span class="btn-outline-neutral-xs max-w-full overflow-hidden">
            <UploadIcon :size="14" />
            <span class="truncate txt-body-s-medium">{{ getLogoFilename }}</span>
          </span>
          <input type="file" class="hidden" :accept="logoAcceptedFormats" @change="handleLogoChange" />
        </label>
      </div>
    </div>
  </div>

  <div class="mt-4 card">
    <h2 class="mb-3 text-headline-m">Header image</h2>
    <hr class="dark:bg-gray-700 my-4 h-px border-0 bg-surface-elevation-5" />
    <div class="flex min-w-0 gap-3">
      <img class="h-20 w-36 shrink-0 self-center rounded-sm object-contain" :src="previewHeaderImageUrl" />
      <div class="flex min-w-0 flex-col justify-center">
        <p class="text-label-m text-subtlest">Upload your company header image as a .jpg or .svg in 1400x200 px.</p>
        <div class="mt-2 flex flex-wrap gap-2">
          <label class="max-w-full min-w-0 cursor-pointer">
            <span class="btn-outline-neutral-xs max-w-full overflow-hidden">
              <UploadIcon :size="14" />
              <span class="truncate txt-body-s-medium">{{ getHeaderImageFilename }}</span>
            </span>
            <input type="file" class="hidden" :accept="headerImageAcceptedFormats" @change="handleHeaderImageChange" />
          </label>
          <button v-if="canRemoveHeaderImage" type="button" class="btn-secondary-destructive-xs" @click="removeHeaderImage">
            <span>
              {{ model.headerImage === null ? t('PartnerCompany.headerImageUndoRemove') : t('PartnerCompany.headerImageRemove') }}
            </span>
            <UndoIcon v-if="model.headerImage === null" class="ml-1 lucide-icon-s" />
            <Trash2Icon v-else class="ml-1 lucide-icon-s" />
          </button>
        </div>
        <div v-if="v$.logos.headerImage.$errors.length" class="mt-2 text-label-m text-error">
          <div v-for="error in v$.logos.headerImage.$errors" :key="error.$message">{{ error.$message }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Trash2Icon, UndoIcon, UploadIcon } from '@ramp106/omrjs-core-ui/icons'
import type { Validation } from '@vuelidate/core'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  v$: Validation
  headerImageUrl?: string | null
  logoUrl?: string | null
  webLogoUrl?: string | null
}>()

const model = defineModel<{
  headerImage: File | null | undefined
  logo: File | undefined
  webLogo: File | undefined
}>({
  default: () => ({
    headerImage: undefined,
    logo: undefined,
    webLogo: undefined,
  }),
})

const headerImageAcceptedFormats = 'image/jpeg,image/svg+xml'
const logoAcceptedFormats = 'application/pdf,application/postscript,image/svg+xml'
const webLogoAcceptedFormats = 'image/png,image/jpg,image/jpeg,image/svg+xml'

const getHeaderImageFilename = computed(() => {
  if (model.value.headerImage === null) return t('PartnerCompany.headerImageWillBeRemoved')

  const filename = model.value.headerImage?.name || ''
  return filename.length ? shortenFileName(filename) : t('PartnerCompany.uploadNewImage')
})

const getLogoFilename = computed(() => {
  const filename = model.value.logo?.name || ''
  return filename.length ? shortenFileName(filename) : t('PartnerCompany.uploadNewImage')
})

const getWebLogoFilename = computed(() => {
  const filename = model.value.webLogo?.name || ''
  return filename.length ? shortenFileName(filename) : t('PartnerCompany.uploadNewImage')
})

const previewLogoUrl = computed(() => {
  return props.logoUrl?.length ? props.logoUrl : '/assets/images/profile-img.png'
})

const previewHeaderImageUrl = computed(() => {
  if (model.value.headerImage === null) return '/assets/images/profile-img.png'

  return props.headerImageUrl?.length ? props.headerImageUrl : '/assets/images/profile-img.png'
})

const previewWebLogoUrl = computed(() => {
  return props.webLogoUrl?.length ? props.webLogoUrl : '/assets/images/profile-img.png'
})

const canRemoveHeaderImage = computed(() => {
  return !!props.headerImageUrl || !!model.value.headerImage
})

function shortenFileName(fileName: string): string {
  const dotIndex = fileName.lastIndexOf('.')
  const namePart = dotIndex !== -1 ? fileName.slice(0, dotIndex) : fileName
  const extPart = dotIndex !== -1 ? fileName.slice(dotIndex) : ''
  const maxTotalLength = 16
  const maxNameLength = maxTotalLength - extPart.length

  const shortName = namePart.length > maxNameLength ? namePart.slice(0, maxNameLength - 1) + '…' : namePart

  return shortName + extPart
}

function handleLogoChange(e: Event) {
  const selected = (e.target as HTMLInputElement).files?.[0] || undefined
  model.value.logo = selected
}

function handleHeaderImageChange(e: Event) {
  const selected = (e.target as HTMLInputElement).files?.[0] || undefined
  model.value.headerImage = selected
}

function removeHeaderImage() {
  model.value.headerImage = model.value.headerImage === null ? undefined : null
}

function handleWebLogoChange(e: Event) {
  const selected = (e.target as HTMLInputElement).files?.[0] || undefined
  model.value.webLogo = selected
}
</script>
